<template>
	<transition name="action-overlay-fade">
		<div v-if="visible" class="action-overlay">
			<div class="action-overlay-content">
				<transition name="action-overlay-icon" mode="out-in">
					<div v-if="!success" key="loading" class="action-overlay-icon-wrapper">
						<font-awesome-icon :icon="displayLoadingIcon" pulse class="action-overlay-spinner" />
						<p class="action-overlay-text mt-3">{{ displayLoadingText }}</p>
					</div>
					<div v-else key="success" class="action-overlay-icon-wrapper">
						<font-awesome-icon :icon="displaySuccessIcon" class="action-overlay-check" />
						<p class="action-overlay-text mt-3">{{ displaySuccessText }}</p>
					</div>
				</transition>
			</div>
		</div>
	</transition>
</template>

<script>
const DEFAULT_LOADING_ICON = ['fas', 'spinner']
const DEFAULT_SUCCESS_ICON = ['far', 'check-circle']

export default {
	name: 'ActionOverlay',
	props: {
		/**
		 * Texte de chargement par défaut pour cet overlay.
		 * Peut être surchargé à chaque appel via show().
		 */
		loadingText: {
			type: String,
			default: ''
		},
		/**
		 * Texte de succès par défaut pour cet overlay.
		 * Peut être surchargé à chaque appel via showSuccess().
		 */
		successText: {
			type: String,
			default: ''
		},
		/**
		 * Icône FontAwesome par défaut affichée pendant le chargement.
		 * Format : ['pack', 'name'].
		 */
		loadingIcon: {
			type: Array,
			default: () => DEFAULT_LOADING_ICON
		},
		/**
		 * Icône FontAwesome par défaut affichée en cas de succès.
		 * Format : ['pack', 'name'].
		 */
		successIcon: {
			type: Array,
			default: () => DEFAULT_SUCCESS_ICON
		}
	},
	data() {
		return {
			visible: false,
			success: false,
			currentLoadingText: '',
			currentSuccessText: '',
			currentLoadingIcon: null,
			currentSuccessIcon: null
		}
	},
	computed: {
		displayLoadingText() {
			return this.currentLoadingText || this.loadingText || this.$t('global.enregistrement_en_cours')
		},
		displaySuccessText() {
			return this.currentSuccessText || this.successText || this.$t('global.enregistrement_succes')
		},
		displayLoadingIcon() {
			return this.currentLoadingIcon || this.loadingIcon
		},
		displaySuccessIcon() {
			return this.currentSuccessIcon || this.successIcon
		}
	},
	mounted() {
		document.body.appendChild(this.$el)
	},
	beforeDestroy() {
		if (this.$el.parentNode) {
			this.$el.parentNode.removeChild(this.$el)
		}
	},
	methods: {
		/**
		 * Normalise un argument show/showSuccess en { text, icon }.
		 *
		 * @param {String|Object} arg Texte simple ou objet { text, icon }.
		 * @returns {Object}
		 */
		_normalize(arg) {
			if (!arg) return { text: '', icon: null }
			if (typeof arg === 'string') return { text: arg, icon: null }
			return { text: arg.text || '', icon: arg.icon || null }
		},
		/**
		 * Affiche l'overlay avec un spinner de chargement.
		 *
		 * @param {String|Object} [arg] Texte ou { text, icon } pour cette occurrence.
		 */
		show(arg) {
			const { text, icon } = this._normalize(arg)
			this.currentLoadingText = text
			this.currentLoadingIcon = icon
			this.success = false
			this.visible = true
		},
		/**
		 * Affiche l'icône de succès puis masque l'overlay après 1.2s.
		 *
		 * @param {String|Object} [arg] Texte ou { text, icon } pour cette occurrence.
		 * @returns {Promise} Résolu quand l'overlay est masqué.
		 */
		showSuccess(arg) {
			const { text, icon } = this._normalize(arg)
			this.currentSuccessText = text
			this.currentSuccessIcon = icon
			return new Promise((resolve) => {
				this.success = true
				setTimeout(() => {
					this.visible = false
					this.$nextTick(() => {
						this.success = false
						this.currentLoadingText = ''
						this.currentSuccessText = ''
						this.currentLoadingIcon = null
						this.currentSuccessIcon = null
						resolve()
					})
				}, 1200)
			})
		},
		/**
		 * Masque immédiatement l'overlay et réinitialise les surcharges.
		 */
		hide() {
			this.visible = false
			this.success = false
			this.currentLoadingText = ''
			this.currentSuccessText = ''
			this.currentLoadingIcon = null
			this.currentSuccessIcon = null
		}
	}
}
</script>
